// ResourceBadges.js — Reusable badges for type, subject/subtopic, and difficulty using centralized icon maps
import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { marathiLabels } from '../../utils/categoryStyles';

import {
  categoryIcons,
  colorClasses,
  badgeBaseClass,
  fallbackIcon // Import fallbackIcon
} from '../../utils/categoryStyles';

const renderBadge = (categoryKey, icon, label, color) => {
  // Determine if this badge's label corresponds to a Numeral System.
  // Use the canonical labels defined in categoryStyles.js.
  const isNumeralSystemLabel = label === "English Numerals" || label === "Devnagiri Numerals";
  return (
    <span
      key={`${categoryKey}-${label}`}
      className={`${badgeBaseClass} ${colorClasses[color]} text-left px-1 py-0.5 text-xs`}
      tabIndex={0}
      aria-label={label}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {icon && React.cloneElement(icon, { className: 'w-3 h-3 mr-0.5 flex-shrink-0' })} {label}
    </span>
  );
};

const ResourceBadges = ({ resource }) => {
  const [showMobileList, setShowMobileList] = useState(false);
  if (!resource) return null;
  const badges = [];

  // Type Badge
  if (resource.content_type && resource.content_type !== "0") {
    const itemKey = resource.content_type.toLowerCase().replace(/\s+/g, '-');
    const icon = categoryIcons.type.items[itemKey]?.icon || categoryIcons.type.items.default.icon;
    const typeColor = categoryIcons.type?.color || 'gray';
    const color = colorClasses[typeColor] ? typeColor : 'gray';
    const label = categoryIcons.type.items[itemKey]?.label || resource.content_type; // Define label correctly
    badges.push(renderBadge('type', icon, label, color));
  }
  
  // Subject/Subtopic Badge
  const subjectValue = resource.subject && resource.subject !== "0" ? resource.subject : null;
  const subtopicValue = resource.subtopic && resource.subtopic !== "0" ? resource.subtopic : null;

  let topicLabel = subtopicValue || subjectValue;
  let topicIcon = fallbackIcon; // Default to fallbackIcon

  if (topicLabel) { // Only proceed if we have a valid subject or subtopic after checking for "0"
    const normalizedSubject = (subjectValue || '').trim().toLowerCase();
    const normalizedSubtopic = (subtopicValue || '').trim().toLowerCase().replace(/\s+/g, '_');

    if (subtopicValue && categoryIcons.subtopic.items[normalizedSubtopic]?.icon) {
      topicIcon = categoryIcons.subtopic.items[normalizedSubtopic].icon;
      if (normalizedSubject === 'marathi' && marathiLabels[normalizedSubtopic]) {
        topicLabel = marathiLabels[normalizedSubtopic];
      } else {
        topicLabel = categoryIcons.subtopic.items[normalizedSubtopic]?.label || subtopicValue;
      }
    } else if (subjectValue && categoryIcons.subject.items[normalizedSubject]?.icon) {
      topicIcon = categoryIcons.subject.items[normalizedSubject].icon;
      topicLabel = categoryIcons.subject.items[normalizedSubject]?.label || subjectValue;
    }
    // The condition `topicLabel && topicIcon !== fallbackIcon` might be more precise
    // if topicLabel could be valid but no specific icon is found beyond fallback.
    // However, if topicLabel is derived, it should be fine.
    badges.push(renderBadge('topic', topicIcon, topicLabel, 'green'));
  }

  // Difficulty Badge// Inside ResourceBadges component, before the "Difficulty Badge" section:
  // console.log(`Processing resource: ${resource.code}, Difficulty: '${resource.difficulty_level}'`); // Debug log, can be removed
  
  
  if (resource.difficulty_level && resource.difficulty_level !== "0") {
    const itemKey = resource.difficulty_level.trim().toLowerCase(); // Added .trim() for robustness
    const icon = categoryIcons.difficulty.items[itemKey]?.icon || categoryIcons.difficulty.items.default.icon;
    const color = categoryIcons.difficulty.items[itemKey]?.color || categoryIcons.difficulty.items.default.color || 'gray';
    const label = categoryIcons.difficulty.items[itemKey]?.label || resource.difficulty_level; // Define label correctly
    badges.push(renderBadge('difficulty', icon, label, color));
  }

  // --- +N more logic ---
  const MAX_BADGES = 3;
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const hasExtra = badges.length > MAX_BADGES;
  const visibleBadges = badges.slice(0, MAX_BADGES);
  const hiddenBadges = hasExtra ? badges.slice(MAX_BADGES) : [];

  // Helper to get plain text from badge (label is after icon)
  const getBadgeLabel = (badge) => {
    if (!badge || !badge.props || !badge.props.children) return '';
    const children = badge.props.children;
    // children: [icon, ' ', label] or [icon, label]
    if (Array.isArray(children)) {
      return children[children.length - 1]?.toString().trim();
    }
    return typeof children === 'string' ? children : '';
  };

  // Tooltip for desktop, inline list for mobile
  const MoreBadge = () => {
    const hiddenLabels = hiddenBadges.map(getBadgeLabel).filter(Boolean);
    if (isMobile) {
      return (
        <span
          className="inline-block px-1 py-0.5 rounded bg-gray-200 text-gray-700 text-xs font-medium cursor-pointer select-none border border-gray-300"
          tabIndex={0}
          onClick={() => setShowMobileList((v) => !v)}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setShowMobileList((v) => !v)}
          aria-label={`Show ${hiddenLabels.length} more badges`}
        >
          +{hiddenLabels.length} more
        </span>
      );
    }
    // Desktop: show tooltip on hover/focus
    return (
      <span className="relative group inline-block">
        <span
          className="px-1 py-0.5 rounded bg-gray-200 text-gray-700 text-xs font-medium cursor-pointer select-none border border-gray-300"
          tabIndex={0}
          aria-label={`Show ${hiddenLabels.length} more badges`}
        >
          +{hiddenLabels.length} more
        </span>
        <div className="absolute z-20 left-1/2 -translate-x-1/2 mt-1 px-2 py-1 rounded bg-gray-800 text-white text-xs shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 whitespace-pre-line min-w-[80px] max-w-[200px] text-center pointer-events-none">
          {hiddenLabels.join('\n')}
        </div>
      </span>
    );
  };

  return (
    <div className="flex flex-wrap justify-start gap-x-0.5 gap-y-1 sm:gap-y-0.5 items-start">
      {visibleBadges}
      {hasExtra && <MoreBadge />}
      {/* Mobile inline list below badges */}
      {isMobile && showMobileList && hasExtra && (
        <div className="w-full mt-1 px-1 py-1 rounded bg-gray-100 border border-gray-200 text-xs text-gray-700">
          {hiddenBadges.map((badge, i) => (
            <div key={i} className="" title={getBadgeLabel(badge)}>{getBadgeLabel(badge)}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourceBadges;
